import { useEffect, useState } from 'react';
import { Autocomplete, Stack, TextField } from '@mui/material';

import { fetchGeography } from '../../../services/syntiro_honey/data_ingestion.service';
import { GeographyCountry, GeographyProvince } from '../../../types/syntiro_honey';

interface LocationSelectProps {
  /** Selected direct country id, or null when none. */
  countryId: string | null;
  /** Selected direct province id, or null when none. */
  provinceId: string | null;
  /**
   * Called with the next coherent pair. Picking a country outside the current
   * province clears the province; clearing the country clears both.
   */
  onChange: (countryId: string | null, provinceId: string | null) => void;
  /**
   * Narrow the options to the geography this company reports from. Omit/null for
   * the whole seeded hierarchy.
   */
  companyId?: string | null;
  /** Disable both controls (the record has a site — the site owns the location). */
  disabled?: boolean;
}

/**
 * Direct country/province pickers for SITE-LESS records (OQ27b): clients report
 * whole-country and whole-province slices with no site, so a record may attach to
 * geography directly — like it attaches to a division or subdivision. The pair
 * stays coherent client-side (a province always belongs to the chosen country);
 * the API enforces the same rules again on save.
 *
 * The list is scoped to the record's company, which is the difference between two
 * plausible choices and fifty: CCH operates in South Africa and Namibia, but the
 * picker offered every seeded country down to Bhutan and Peru
 * (Maxine 2026-08-14 ⑦).
 * @returns {JSX.Element}
 */
export default function LocationSelect({
  countryId,
  provinceId,
  onChange,
  companyId = null,
  disabled = false
}: LocationSelectProps) {
  const [countries, setCountries] = useState<Array<GeographyCountry>>([]);
  const [geographyUnavailable, setGeographyUnavailable] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    setGeographyUnavailable(false);
    fetchGeography(companyId ?? undefined)
      .then((items) => {
        if (!cancelled) {
          setCountries(items);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setGeographyUnavailable(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const selectedCountry = countries.find((country) => country.id === countryId) ?? null;
  const provinces = selectedCountry?.provinces ?? [];
  const selectedProvince = provinces.find((province) => province.id === provinceId) ?? null;

  const handleCountry = (next: GeographyCountry | null): void => {
    // A province never outlives its country: keep it only when the new country
    // still contains it.
    const keepProvince =
      next !== null &&
      provinceId !== null &&
      next.provinces.some((province) => province.id === provinceId);
    onChange(next?.id ?? null, keepProvince ? provinceId : null);
  };

  const handleProvince = (next: GeographyProvince | null): void => {
    onChange(countryId, next?.id ?? null);
  };

  return (
    <Stack direction="row" spacing={2}>
      <Autocomplete
        size="small"
        fullWidth
        options={countries}
        value={selectedCountry}
        disabled={disabled || geographyUnavailable}
        onChange={(_event, next) => handleCountry(next)}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, current) => option.id === current.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Country"
            helperText={geographyUnavailable ? 'Unavailable — try again shortly' : undefined}
          />
        )}
      />
      <Autocomplete
        size="small"
        fullWidth
        options={provinces}
        value={selectedProvince}
        // A province needs its country first (the API derives country from
        // province, but the picker reads clearer scoped to one country).
        disabled={disabled || geographyUnavailable || selectedCountry === null}
        onChange={(_event, next) => handleProvince(next)}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, current) => option.id === current.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Province"
            helperText={geographyUnavailable ? 'Unavailable — try again shortly' : undefined}
          />
        )}
      />
    </Stack>
  );
}
